import React from 'react';
import { Info } from 'lucide-react';
import {
  CATEGORY_CONFIDENCE_ROWS,
  CUSTOMER_DISCLAIMER,
  customerConfidenceLabel,
  type CategoryConfidence,
  type CustomerConfidenceLabel,
} from '../utils/customerReportLabels';

export type { CategoryConfidence };

type ConfidenceTier = 'High' | 'Moderate' | 'Limited';

function confidenceBannerStyles(tier: ConfidenceTier): string {
  switch (tier) {
    case 'High':
      return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20';
    case 'Moderate':
      return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20';
    default:
      return 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20';
  }
}

export function resolveOverallConfidenceTier(
  categoryConfidence?: CategoryConfidence | null
): ConfidenceTier {
  const overall = customerConfidenceLabel(categoryConfidence?.overall_confidence);
  if (overall === 'High') return 'High';
  if (overall === 'Moderate') return 'Moderate';
  return 'Limited';
}

interface ReportConfidenceBannerProps {
  tier: ConfidenceTier;
  categoryConfidence?: CategoryConfidence | null;
  playsDetected?: number;
}

export const ReportConfidenceBanner: React.FC<ReportConfidenceBannerProps> = ({
  tier,
  categoryConfidence,
  playsDetected,
}) => {
  const rows = CATEGORY_CONFIDENCE_ROWS.map(({ key, label }) => {
    const raw = categoryConfidence?.[key];
    const display = customerConfidenceLabel(raw);
    if (!display) return null;
    return { label, display };
  }).filter(Boolean) as { label: string; display: CustomerConfidenceLabel }[];

  return (
    <div className={`card mb-8 border-2 ${confidenceBannerStyles(tier)}`}>
      <div className="flex items-start gap-3">
        <Info className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
        <div className="w-full">
          <p className="font-semibold text-secondary-900 dark:text-white">
            Analysis confidence: {tier}
          </p>

          {playsDetected != null && playsDetected < 10 && (
            <p className="mt-2 text-sm text-secondary-700 dark:text-secondary-300">
              Confidence reflects how much usable gameplay Scoutlytics could review in this upload.
            </p>
          )}

          {rows.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-secondary-800 dark:text-secondary-200">
                Category confidence
              </p>
              <ul className="mt-1 text-sm text-secondary-700 dark:text-secondary-300 space-y-0.5">
                {rows.map(({ label, display }) => (
                  <li key={label}>
                    {label} — {display}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-3 text-sm text-secondary-600 dark:text-secondary-400">
            {CUSTOMER_DISCLAIMER}
          </p>
        </div>
      </div>
    </div>
  );
};
